import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

import { EMPTY, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

import { AuthenticationService } from '@common/services';
import { FileContent, FileForViewer } from '@repositories/models';
import { RepositoryFolderService, RepositoryService } from '@repositories/services';

/**
 * Muestra un archivo de Belivpedia dentro de un popup, sin sacar al usuario de
 * la conversación. Replica lo que hace `RepositoryViewer` en la ruta
 * `/apps/repositories/view/:id`, pero sin depender del router.
 */
@Component({
  selector: 'app-chat-file-preview-modal',
  templateUrl: './chat-file-preview-modal.component.html',
  styleUrls: ['./chat-file-preview-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class ChatFilePreviewModalComponent implements OnInit, OnDestroy {

  /** Id del archivo en Belivpedia (el `reference_id` de la citación) */
  @Input() fileId: string;

  public title: string = '';
  public fileType: string;
  public url: string | SafeResourceUrl;
  public textFileContent: string;
  public htmlContent: SafeHtml | null = null;
  public loadingPercent: number = 0;
  public errorKey: string = null;

  private file: FileContent;
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   * @param modal
   * @param _repositoryFolderService
   * @param _repositoryService
   * @param _authenticationService
   * @param _translateService
   * @param _sanitizer
   * @param _router
   * @param _cdr
   */
  constructor(
    public modal: NgbActiveModal,
    private _repositoryFolderService: RepositoryFolderService,
    private _repositoryService: RepositoryService,
    private _authenticationService: AuthenticationService,
    private _translateService: TranslateService,
    private _sanitizer: DomSanitizer,
    private _router: Router,
    private _cdr: ChangeDetectorRef
  ) {
    this._unsubscribeAll = new Subject();
  }

  /**
   * OnInit
   */
  ngOnInit(): void {
    this.loadFile();
  }

  /**
   * OnDestroy
   */
  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  /**
   * Abre el archivo en la página completa de Belivpedia y cierra el popup.
   */
  openInPage(): void {
    this.modal.close();
    this._router.navigate(['/apps/repositories/view/', this.fileId]);
  }

  /**
   * Descarga el archivo mostrado.
   */
  async download(): Promise<void> {
    if (!this.file) return;
    await this._repositoryService.download(this.file.fileName, this.file.title);
  }

  /**
   * Etiqueta localizada sin depender de claves nuevas de traducción.
   * @param {string} es Texto en español
   * @param {string} en Texto en inglés
   */
  label(es: string, en: string): string {
    return this._translateService.currentLang === 'en' ? en : es;
  }

  /**
   * Obtiene la metadata del archivo y luego su contenido para el visor.
   */
  private loadFile(): void {
    this._repositoryFolderService.getFile(this.fileId)
      .pipe(
        takeUntil(this._unsubscribeAll),
        switchMap(file => {
          if (!file) {
            this.errorKey = 'COMMON.ACTIONS.FAIL_LOADING_OBJECT';
            this._cdr.detectChanges();
            return EMPTY;
          }

          // Archivo eliminado
          if (!file.status) {
            this.errorKey = 'BELIVPEDIA.MESSAGES.LIBRARY_DELETED';
            this._cdr.detectChanges();
            return EMPTY;
          }

          // Archivo privado: solo el dueño o los usuarios autorizados lo ven
          const currentUserId = this._authenticationService.currentUserValue?.id;
          const isOwner = file.owner?.id === currentUserId;
          const isInViewUsers = file.viewUsers?.some((u: any) => u.id === currentUserId) ?? false;
          if (file.private_ && !isOwner && !isInViewUsers) {
            this.errorKey = 'BELIVPEDIA.MESSAGES.LIBRARY_PRIVATE';
            this._cdr.detectChanges();
            return EMPTY;
          }

          this.file = file;
          this.title = file.title;

          // Los archivos de tipo URL no se pueden visualizar: se abren aparte
          if (file.contentType === 'URL') {
            window.open(file.url, '_blank');
            this.modal.close();
            return EMPTY;
          }

          this._cdr.detectChanges();
          return this._repositoryService.getFile(file.fileName, file.title);
        })
      )
      .subscribe({
        next: res => {
          // El servicio emite el % de descarga y al final el archivo listo
          if (typeof res === 'number') {
            this.loadingPercent = res;
            this._cdr.detectChanges();
            return;
          }

          if (!res) return;

          const file = res as FileForViewer;
          this.url = file.url;
          this.fileType = file.fileType;

          if (file.fileType === 'txt') {
            file.blob.text().then(text => {
              this.textFileContent = text;
              this._cdr.detectChanges();
            });
          }

          if (file.fileType === 'html') {
            file.blob.text().then(text => {
              this.htmlContent = this._sanitizer.bypassSecurityTrustHtml(text);
              this._cdr.detectChanges();
            });
          }

          this._cdr.detectChanges();
        },
        error: () => {
          this.errorKey = 'COMMON.ACTIONS.FAIL_LOADING_OBJECT';
          this._cdr.detectChanges();
        }
      });
  }
}
